"use client";

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { AlertCircle, CheckCircle, ArrowLeft, KeyRound } from "lucide-react";
import { authService } from "../../apis/authService";
import { useAuth } from "../../context/AuthContext";

export default function OtpVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const purpose = searchParams.get("purpose") || "login";
  const userId = searchParams.get("userId");
  const email = searchParams.get("email") || "";
  const remember = searchParams.get("remember") === "1";

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const inputRefs = useRef([]);

  // Recovery code mode (only for TOTP purpose)
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const recoveryInputRef = useRef(null);

  useEffect(() => {
    if (useRecoveryCode) {
      recoveryInputRef.current?.focus();
    } else if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [useRecoveryCode]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(0, 1);
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  // Paste handler
  const handlePaste = (index, event) => {
    event.preventDefault();
    const paste = event.clipboardData?.getData("text") || "";
    const digits = paste.replace(/\D/g, "").slice(0, 6 - index).split("");

    if (digits.length === 0) return;

    setOtp((prev) => {
      const next = [...prev];
      digits.forEach((digit, idx) => {
        next[index + idx] = digit;
      });
      return next;
    });

    const nextIndex = Math.min(index + digits.length - 1, 5);
    const nextInput = inputRefs.current[nextIndex];
    nextInput?.focus();
    nextInput?.select();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      setMessage({ type: "error", text: "Please enter the complete 6-digit code" });
      return;
    }
    setIsLoading(true);
    setMessage(null);
    try {
      let result;
      if (purpose === "totp") {
        result = await authService.verifyLoginTotp(userId, otpCode, remember);
      } else if (purpose === "login") {
        result = await authService.verifyLoginOtp(userId, otpCode, remember);
      } else {
        result = await authService.verifySignupOtp(userId, otpCode);
      }
      if (result.success) {
        await refreshMe();
        setMessage({ type: "success", text: "Verification successful! Redirecting..." });
        setTimeout(() => navigate(purpose === "signup" ? "/onboarding" : "/scribe"), 1500);
      } else {
        setMessage({ type: "error", text: result.message || "Invalid or expired OTP" });
      }
    } catch {
      setMessage({ type: "error", text: "Verification failed. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecoveryCodeSubmit = async (e) => {
    e.preventDefault();
    const trimmed = recoveryCode.trim();
    if (!trimmed) {
      setMessage({ type: "error", text: "Please enter a recovery code" });
      return;
    }
    setIsLoading(true);
    setMessage(null);
    try {
      const result = await authService.verifyLoginRecoveryCode(userId, trimmed, remember);
      if (result.success) {
        await refreshMe();
        const remaining = result.recoveryCodesRemaining;
        const warningText = typeof remaining === "number" && remaining <= 2
          ? ` (${remaining} recovery code${remaining === 1 ? "" : "s"} remaining — consider regenerating them)`
          : "";
        setMessage({ type: "success", text: `Login successful! Redirecting...${warningText}` });
        setTimeout(() => navigate("/scribe"), 1500);
      } else {
        setMessage({ type: "error", text: result.message || "Invalid recovery code" });
      }
    } catch {
      setMessage({ type: "error", text: "Verification failed. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    setMessage(null);
    try {
      const result = await authService.resendOtp(userId, purpose);
      if (result.success) {
        setMessage({ type: "success", text: "OTP resent successfully" });
        setCountdown(60);
      } else {
        setMessage({ type: "error", text: result.message || "Failed to resend OTP" });
      }
    } catch {
      setMessage({ type: "error", text: "Failed to resend OTP" });
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const toggleRecoveryMode = () => {
    setUseRecoveryCode(!useRecoveryCode);
    setMessage(null);
    setRecoveryCode("");
    setOtp(["", "", "", "", "", ""]);
  };

  return (
    <div className="space-y-5">
      {message && (
        <div className={`p-3 rounded-lg flex items-center gap-2 ${message.type === "success"
            ? "bg-green-50 text-green-800 border border-green-200"
            : "bg-red-50 text-red-800 border border-red-200"
          }`}>
          {message.type === "success" ? (
            <CheckCircle className="w-4 h-4 text-green-600" />
          ) : (
            <AlertCircle className="w-4 h-4 text-red-600" />
          )}
          <span className="text-base font-medium">{message.text}</span>
        </div>
      )}

      {/* ─── Recovery Code Mode ─── */}
      {purpose === "totp" && useRecoveryCode ? (
        <>
          <div className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-3">
              <KeyRound className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Use a Recovery Code</h3>
            <p className="text-base sm:text-lg text-gray-500 mt-1.5">
              Enter one of the recovery codes you saved when setting up two-factor authentication
            </p>
          </div>

          <form onSubmit={handleRecoveryCodeSubmit}>
            <div className="my-4">
              <input
                ref={recoveryInputRef}
                type="text"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX-XX"
                className="w-full h-14 text-center text-lg font-mono font-semibold tracking-wider border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                maxLength={14}
                autoComplete="off"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-[52px] rounded-lg bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white font-medium transition disabled:opacity-70"
            >
              {isLoading ? "Verifying..." : "Verify Recovery Code"}
            </button>
          </form>

          <div className="text-center">
            <button
              onClick={toggleRecoveryMode}
              className="text-base text-teal-600 hover:underline font-semibold"
            >
              Use authenticator app instead
            </button>
          </div>
        </>
      ) : (
        <>
          {/* ─── Normal OTP / TOTP Mode ─── */}
          <div className="text-center">
            {purpose === "totp" ? (
              <>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Two-Factor Authentication</h3>
                <p className="text-base sm:text-lg text-gray-500 mt-1.5">
                  Enter the 6-digit verification code generated by your Authenticator app
                </p>
              </>
            ) : (
              <>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Verify your code</h3>
                <p className="text-base sm:text-lg text-gray-500 mt-1.5">
                  We sent a 6-digit code to <span className="font-semibold text-gray-700">{email || "your email"}</span>
                </p>
              </>
            )}
          </div>

          <form onSubmit={handleVerify}>
            <div className="flex justify-center gap-3 my-6">
              {otp.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => (inputRefs.current[idx] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(idx, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(idx, e)}
                  onPaste={(e) => handlePaste(idx, e)}
                  className="w-12 h-14 text-center text-xl font-semibold border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-[52px] rounded-lg bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white font-medium transition disabled:opacity-70"
            >
              {isLoading ? "Verifying..." : "Verify Code"}
            </button>
          </form>

          {/* Recovery code link — only for TOTP */}
          {purpose === "totp" && (
            <div className="text-center">
              <button
                onClick={toggleRecoveryMode}
                className="text-base text-gray-500 hover:text-teal-600 hover:underline transition-colors"
              >
                Lost your authenticator? Use a recovery code
              </button>
            </div>
          )}

          {purpose !== "totp" && (
            <div className="text-center text-base">
              {countdown > 0 ? (
                <span className="text-gray-500">
                  Resend code in <span className="font-mono">{formatTime(countdown)}</span>
                </span>
              ) : (
                <button onClick={handleResend} className="text-teal-600 hover:underline font-semibold text-base">
                  Resend code
                </button>
              )}
            </div>
          )}
        </>
      )}

      <div className="border-t border-gray-200 pt-5 flex flex-col gap-2.5 text-center text-base">
        {purpose !== "totp" && !useRecoveryCode && (
          <Link
            to={purpose === "login" ? "/signin" : "/signup"}
            className="text-teal-600 hover:underline font-medium"
          >
            Use a different email
          </Link>
        )}
        <Link
          to="/signin"
          className="inline-flex items-center justify-center gap-1.5 text-gray-500 hover:text-gray-700 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Sign In
        </Link>
      </div>
    </div>
  );
}